<template>
  <div class="login-wrap" data-testid="login-page">
    <div class="otp-back" v-show="step === 2" @click="back">
      <i class="fas fa-arrow-left fa-fw"></i>
      <span>Back</span>
    </div>
    <div class="lg-title" data-testid="login-title">
      <span v-if="step === 1">What's your email or phone number?</span>
      <span v-else>OTP Verification</span>
    </div>

    <div v-show="step === 1">
      <form class="form-mobx" @submit.prevent="handleContinue" data-testid="login-form">
        <!-- Combined input: country selector appears only in phone mode -->
        <div class="input-shell" :class="{ 'is-phone': isPhoneMode, 'is-focused': focused }" data-testid="input-shell">
          <!-- Country code dropdown (only in phone mode) -->
          <div v-if="isPhoneMode" class="country-select" data-testid="country-select-wrapper">
            <button type="button" class="country-trigger" @click="toggleCountryMenu" data-testid="country-trigger"
              aria-haspopup="listbox" :aria-expanded="countryMenuOpen ? 'true' : 'false'">

              <span class="flag" data-testid="country-flag">
                <div :class="'iti-flag ' + selectedCountry.code"></div>
              </span>
              <svg class="chev" :class="{ rot: countryMenuOpen }" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <ul v-if="countryMenuOpen" class="country-menu" role="listbox" data-testid="country-menu">
              <li class="search-country">
                <input type="text" v-model="countrySearch" placeholder="Type your country.." ref="countrySearch" />
              </li>
              <li role="option" v-for="c in countryList" :key="c.code"
                class="country-item" :class="{ active: c.code === selectedCountry.code }"
                :aria-selected="c.code === selectedCountry.code ? 'true' : 'false'"
                @click="selectCountry(c)" :data-testid="`country-item-${c.code}`"
                v-show="countryFilter(c.name)">
                <div :class="'iti-flag ' + c.code"></div>
                <span class="c-name">{{ cleanCountryName(c.name) }}</span>
                <span class="c-dial">+{{ c.dialCode }}</span>
              </li>
            </ul>
          </div>
          <div v-if="isPhoneMode" class="country-select phone-code-txt">
            <span class="dial" data-testid="country-dial">+{{selectedCountry.dialCode}}</span>
          </div>

          <input ref="theInput" v-model="value" autocomplete="off" spellcheck="false"
            class="text-input" data-testid="login-input"
            @focus="focused = true" @blur="focused = false"
            @input="onInput" @keydown="onKeydown"
            type="tel" inputmode="tel"
            :placeholder="placeholder"
            v-if="isPhoneMode"
          />
          <input ref="theInput" v-model="value" autocomplete="off" spellcheck="false"
            class="text-input" data-testid="login-input"
            @focus="focused = true" @blur="focused = false"
            @input="onInput" @keydown="onKeydown"
            type="text" inputmode="email"
            :placeholder="placeholder"
            v-else
          />
        </div>

        <div class="mobx-error-box" v-if="errorMsg">{{ errorMsg }}</div>

        <button type="submit" class="continue-btn brand-bg" :disabled="!canSubmit" data-testid="continue-button">
          Continue
        </button>
      </form>

      <div class="or-div">
        <span>{{ $t('searchForm.or') }}</span>
      </div>
      <div class="corporate-login-signup">
        <span>
          {{ $t('searchForm.haveCorporate') }}
          <a @click="loginSignup" class="next-line-mobile">{{ $t('searchForm.loginSignup') }}</a>
        </span>
      </div>
    </div>

    <div v-show="step === 2" class="verify-box">
      <span class="vfy-label">
        We have sent the verification code to
        <b v-if="isPhoneMode">+{{ selectedCountry.dialCode }}{{ value }}</b>
        <b v-else>{{ value }}</b>
      </span>
      <otp :digit-count="4" :auto-verify="true" v-model="verificationCode" @complete="verifyCode" ref="otpField"/>
      <div :class="['resend-code', {'-enable':!resendCountdown}]" @click="resendCode">
        Resend Code
        <span v-if="resendCountdown">({{ resendCountdown }}s)</span>
      </div>
      <button class="continue-btn brand-bg" @click="verifyCode">
        Confirm
      </button>
    </div>

    <login-signup @corporateAuthSuccess="corporateAuthSuccess"></login-signup>
  </div>
</template>

<script>
const countries = require('@/data/countries')
import otp from "./otp.vue"
import LoginSignup from '@/components/SearchForm/LoginSignupModal'

export default {
  name: 'LoginPage',
  components: { otp, LoginSignup },
  data () {
    return {
      value: '',
      focused: false,
      countries: countries,
      selectedCountry: countries['us'],
      countryMenuOpen: false,
      countrySearch: '',
      step: 1,
      verificationCode: '',
      lastToken: '',
      errorMsg: '',
      phoneCodeLoading: false,
      doubleSubmit: false,
      codeDelay: ''
    }
  },
  computed: {
    // Phone mode if first non-space character is a digit, +, or (
    isPhoneMode () {
      const v = (this.value || '').trim()
      if (!v) return false
      const first = v.charAt(0)
      return /[0-9+(]/.test(first)
      // return true
    },
    placeholder () {
      return this.isPhoneMode
        ? 'Phone number'
        : 'Enter phone number or email'
    },
    countryList () {
      let country = Object.values(countries)
      let displayCountry = []
      country = country.sort((a, b) => (a.name > b.name) ? 1 : -1)

      let topCountries = ['us', 'ca', 'gb']
      topCountries.forEach(x => {
        displayCountry.push(...country.filter(y => y.code === x))
      })
      topCountries.forEach(x => {
        country = country.filter(y => y.code !== x)
      })
      displayCountry.push(...country)

      return displayCountry
    },
    canSubmit () {
      const v = (this.value || '').trim()
      if (!v) return false
      if (this.isPhoneMode) {
        // require at least 4 digits in phone mode
        const digits = v.replace(/\D/g, '')
        return digits.length >= 4
      }
      // email/text mode: must look like an email or be at least 3 chars
      return v.length >= 3
    },
    resendCountdown () { return this.$store.state.user.resend.countdown },
    other () { return this.$store.state.other }
  },
  methods: {
    back () {
      this.step = 1
      this.verificationCode = ''
    },
    onInput () {
      // No transformation; isPhoneMode is computed from current value.
    },
    onKeydown (e) {
      // In phone mode, restrict to digits, spaces, dashes, parens, plus, backspace, etc.
      this.errorMsg = ''
      if (this.isPhoneMode) {
        const allowed = [
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'Tab',
          'Home',
          'End'
        ]
        if (allowed.includes(e.key)) return
        if (e.metaKey || e.ctrlKey) return

        // If the user has the whole value selected and types a single non-digit
        // character, let it through so it REPLACES the digits and the field
        // flips back to email/text mode.
        const input = e.target
        const fullySelected =
          input.selectionStart === 0 &&
          input.selectionEnd === input.value.length &&
          input.value.length > 0
        if (fullySelected && e.key.length === 1) {
          return
        }

        if (!/^[0-9 +()\-]$/.test(e.key)) { // eslint-disable-line no-useless-escape
          e.preventDefault()
        }
      } else {
        this.countrySearch = ''
      }
    },
    toggleCountryMenu (e) {
      let self = this
      e.stopPropagation()
      self.countryMenuOpen = !this.countryMenuOpen
      setTimeout(() => {
        if (self.countryMenuOpen && self.$refs.countrySearch) {
          self.$refs.countrySearch.focus()
        }
      }, 100)
    },
    cleanCountryName (x) {
      return x.replace(/\(.*?\)/g, "")
    },
    countryFilter (x) {
      let countryName = x.toLowerCase()
      let regex = new RegExp(this.countrySearch, 'gi')
      if (countryName.match(regex) || this.countrySearch === null || this.countrySearch === '') return true
      return false
    },
    selectCountry (c) {
      this.selectedCountry = c
      this.$store.commit('other/setOtherData', {defaultCountry: c.code})
      this.countryMenuOpen = false
      this.$nextTick(() => {
        if (this.$refs.theInput) this.$refs.theInput.focus()
      })
    },
    handleOutsideClick (e) {
      if (!this.countryMenuOpen) return
      if (!this.$el.contains(e.target)) {
        this.countryMenuOpen = false
        return
      }
      // close if clicked outside the country-select area
      const wrap = this.$el.querySelector('.country-select')
      if (wrap && !wrap.contains(e.target)) {
        this.countryMenuOpen = false
      }
    },
    handleContinue () {
      let self = this
      if (!self.canSubmit || self.doubleSubmit) return

      self.errorMsg = ''
      if (!self.isPhoneMode) {
        let pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        if (self.value && !pattern.test(self.value)) {
          self.errorMsg = self.$t('common.invalidEmail')
          return
        }
      }

      self.doubleSubmit = true
      self.$root.$emit('generateToken2', token => {
        self.doubleSubmit = false
        self.lastToken = token
        self.$store.commit('global/setLoaderData', {show: true})
        if (self.isPhoneMode) self.getPhoneCode()
        else {
          if (self.codeDelay) clearTimeout(self.codeDelay)
          self.codeDelay = setTimeout(() => {
            self.getEmailCode()
          }, 200)
        }
      })
    },
    getPhoneCode () {
      let self = this
      if (self.resendCountdown && self.other.lastPhoneOtp === `${this.selectedCountry.dialCode}${this.value.trim()}`) {
        self.$store.commit('global/setLoaderData', {show: false})
        self.step = 2
        return
      }
      let countryCode = this.selectedCountry.dialCode.toString()
      if (countryCode.indexOf('+') > -1) countryCode = countryCode.substr(1)
      let expected = (`${this.value.trim()}${countryCode.replaceAll('+', '')}` >> 2) * 5
      self.$store.commit('global/setLoaderData', {show: true})
      if (this.$refs.otpField) this.$refs.otpField.resetOTP()

      if (self.phoneCodeLoading) return
      self.phoneCodeLoading = true

      self.$sdk.post(self, '/auth/login/phone/code', {
        phone: this.value.trim(),
        countryCode: this.selectedCountry.dialCode,
        code: expected,
        d2d: 1
        // nationalNumber: this.value.trim()
      }, res => {
        self.phoneCodeLoading = false
        self.$store.commit('global/setLoaderData', {show: false})
        if (res.error) {
          self.errorMsg = res.error.message.replaceAll('backend.', '')
          // self.$store.commit('global/setAlertData', {show: true, message: res.error.message})
          return
        }
        if (res.notes) {
          self.$store.commit('global/setAlertData', {show: true, message: self.$t('global.oops')})
          return
        }

        self.$store.commit('other/setOtherData', {
          lastPhoneOtp: `${this.selectedCountry.dialCode}${this.value.trim()}`
        })
        self.step = 2
        self.$store.commit('user/setResendReady', false)
      }, '1.1')
    },
    getEmailCode () {
      let self = this
      self.$store.commit('global/setLoaderData', {show: true})
      if (this.$refs.otpField) this.$refs.otpField.resetOTP()
      self.$sdk.post(self, '/auth/login/email/code', {
        email: this.value,
        brand: this.$store.state.brandCode,
        d2d: 1
      }, res => {
        setTimeout(() => {
          self.$store.commit('global/setLoaderData', {show: false})
          if (res.error) {
            self.errorMsg = res.error.message.replaceAll('backend.', '')
            // self.$store.commit('global/setAlertData', {show: true, message: res.error.message})
            return
          }
          self.$store.commit('global/setAlertData', {
            show: true,
            message: `Verification code sent to <b>${this.value}</b>.`
          })
          if (self.step !== 2) self.step = 2
          self.$store.commit('user/setResendReady', false)
        }, 1000)
      }, '1.1')
    },
    resendCode () {
      if (this.resendCountdown) return
      this.verificationCode = ''
      if (this.isPhoneMode) this.getPhoneCode()
      else {
        console.log(2)
        this.getEmailCode()
      }
    },
    verifyCode () {
      if (this.isPhoneMode) this.verifyPhoneCode()
      else this.verifyEmailCode()
    },
    verifyPhoneCode () {
      let self = this
      self.$store.commit('global/setLoaderData', {show: true})

      let phone = `a${this.selectedCountry.dialCode}${this.value.trim()}`
      if (self.other.attemptCount[phone] && self.other.attemptCount[phone] >= 10) {
        self.$store.commit('global/setLoaderData', {show: false})
        self.$store.commit('global/setAlertData', {show: true, message: 'Sorry, too many request, please try again later.'})
        return
      }
      self.$store.commit('other/setOtherData', {lastCountryCode: this.selectedCountry.dialCode})
      self.$sdk.post(self, '/auth/login/phone/verify', {
        phone: this.value.trim(),
        countryCode: this.selectedCountry.dialCode,
        smsCode: self.verificationCode,
        device: self.lastToken,
        d2d: 1
      }, res => {
        self.$store.commit('global/setLoaderData', {show: false})
        if (res.error) {
          self.$store.commit('other/addAttemptCount', `a${this.selectedCountry.dialCode}${this.value.trim()}`)
          self.$store.commit('global/setAlertData', {show: true, message: res.error.message.replaceAll('backend.', '')})
          return
        }

        let tmpMobile = `${self.selectedCountry.dialCode}${self.value.trim()}`
        self.$store.commit('other/addVerifiedMobile', tmpMobile)
        self.$store.commit('other/setOtherData', {loginVia: 'phone'})

        if (res.new) {
          self.$emit('nonUser', {
            loginVia: 'phone',
            phone: self.value.trim(),
            countryCode: self.selectedCountry.dialCode
          })
        } else {
          res.token = self.lastToken
          self.$emit('loginUser', res)
        }
        self.resetForm()
      }, '1.1')
    },
    verifyEmailCode () {
      let self = this
      self.$store.commit('global/setLoaderData', {show: true})

      let email = this.value.trim()
      if (self.other.attemptCount[email] && self.other.attemptCount[email] >= 10) {
        self.$store.commit('global/setLoaderData', {show: false})
        self.$store.commit('global/setAlertData', {show: true, message: 'Sorry, too many request, please try again later.'})
        return
      }
      self.$sdk.post(self, '/auth/login/email/verify', {
        email: this.value,
        code: self.verificationCode,
        d2d: 1
      }, res => {
        self.$store.commit('global/setLoaderData', {show: false})
        if (res.error) {
          self.$store.commit('other/addAttemptCount', this.value.trim())
          self.$store.commit('global/setAlertData', {show: true, message: res.error.message.replaceAll('backend.', '')})
          return
        }
        self.$store.commit('other/setOtherData', {loginVia: 'email'})
        console.log(41)
        if (res.token) {
          console.log(42)
          self.$emit('getUserInfo', res.token)
          self.resetForm()
          return
        }
        console.log(43)
        self.$emit('nonUser', {
          loginVia: 'email',
          email: self.value.trim()
        })
        self.resetForm()
      }, '1.1')
    },
    resetForm () {
      this.step = 1
      this.verificationCode = ''
      this.$store.commit('user/setResendReady', true)
    },
    loginSignup () {
      let self = this
      self.$store.commit('loginSignup/showModal')
      document.getElementsByTagName('body')[0].setAttribute('style', 'overflow: hidden!important;')
      document.body.classList.add('widget-no-scroll')
      if (document.querySelectorAll('.top-bar.dynamic')[0]) {
        document.querySelectorAll('.top-bar.dynamic')[0].setAttribute('style', 'z-index:2;')
      }
      if (document.querySelectorAll('#desktopMenu')[0]) {
        document.querySelectorAll('#desktopMenu')[0].setAttribute('style', 'z-index:100;')
        document.querySelectorAll('#mobileMenu')[0].setAttribute('style', 'z-index:100;')
      }
    },
    corporateAuthSuccess () {
      if (this.$store.state.user.token) {
        this.$emit('hideVeriForm')
      }
    }
  },
  watch: {
    // When the input mode flips (text<->phone) the country-select element is
    // inserted/removed before the input, which causes the browser to drop
    // focus on the input. Re-focus it immediately and keep the caret at the end.
    isPhoneMode () {
      this.$nextTick(() => {
        const el = this.$refs.theInput
        if (!el) return
        el.focus()
        const len = el.value.length
        try {
          el.setSelectionRange(len, len)
        } catch (_) {
          /* setSelectionRange not supported on some input types — safe to ignore */
        }
      })
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)

    if (this.other.defaultCountry) {
      this.selectedCountry = countries[this.other.defaultCountry.toLowerCase()]
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleOutsideClick)
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/vendor/intlTelInput/css/intlTelInput.css";
.login-wrap {
  width: 100%;
  max-width: 420px;
  margin: 32px auto;
}

.otp-back{
  letter-spacing: .4px;
  margin-bottom: 15px;
  display: inline-block;
  cursor: pointer;
  color: #777;
}

.lg-title {
  font-size:32px;
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 5px;
  color: #000;
  font-family: 'Questrial', sans-serif;
}

.form-mobx {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-shell {
  display: flex;
  align-items: stretch;
  background: #eeeeee;
  border-radius: 8px;
  overflow: visible;
  position: relative;
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.input-shell.is-focused {
  background: #e3e3e3;
  box-shadow: 0 0 0 2px #000 inset;
}

.country-select {
  position: relative;
  display: flex;
  align-items: stretch;
  border-right: 1px solid #d6d6d6;
}

.country-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 56px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: #000;
}

.country-trigger:hover {
  background: rgba(0, 0, 0, 0.05);
}

.flag {
  font-size: 18px;
  line-height: 1;
}

.dial {
  font-variant-numeric: tabular-nums;
}

.chev {
  width: 16px;
  height: 16px;
  margin-left: 2px;
  transition: transform 0.18s ease;
}

.chev.rot {
  transform: rotate(180deg);
}

.country-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 320px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
  margin: 0;
  padding: 6px 0;
  list-style: none;
  z-index: 30;
}
.search-country{
  padding: 5px 10px;
  input{
    width: 100%;
    height: 30px;
    outline: none;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 0 5px;
  }
}

.country-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #000;
}

.country-item:hover,
.country-item.active {
  background: #f3f3f3;
}

.c-name {
  flex: 1;
}

.c-dial {
  color: #545454;
  font-variant-numeric: tabular-nums;
}

.phone-code-txt{
  border: none;
  align-items: center;
  padding-left: 10px;
  color: #888;
  font-size: 16px;
}

.text-input {
  flex: 1;
  min-width: 0;
  height: 56px;
  padding: 0 16px;
  background: transparent;
  border: 0;
  outline: none;
  font-size: 16px;
  color: #000;
  box-shadow: none !important;
  margin: 0 !important;
}

.text-input::placeholder {
  color: #6b6b6b;
}

.mobx-error-box{
  font-size: 14px;
  letter-spacing: .4px;
  color: #e56262;
  position: relative;
  bottom: 8px;
}

.continue-btn {
  height: 52px;
  border-radius: 8px;
  background: #000;
  color: #fff;
  border: 0;
  cursor: pointer;
  font-size: 17px;
  font-weight: 600;
  transition: background-color 0.18s ease, transform 0.06s ease;
}

.continue-btn:hover:not(:disabled) {
  background: #1a1a1a;
}

.continue-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.continue-btn:disabled {
  background: #d6d6d6;
  color: #8a8a8a;
  cursor: not-allowed;
}

.alt-ico {
  width: 18px;
  height: 18px;
}

.otp-field{
  background: #eee;
  border-radius: 8px;
  height: 56px;
  border: 0;
  outline: none;
  padding:0 16px;
  line-height: 56px;
  width: 100%;
}
.vfy-label, .resend-code{
  color: #aaa;
  font-size: 14px;
  letter-spacing: .4px;
}
.resend-code.-enable{
  color: #306fd1;
  font-weight: 600;
  cursor: pointer;
}
.verify-box{
  .continue-btn{
    width: 100%;
    margin-top: 15px;
  }
}

.or-div {
  position: relative;
  z-index: 1;
  text-align: center;
  margin: 20px 0;

  span {
    position: relative;
    z-index: 2;
    background: #fff;
    padding: 0 10px;
    font-size: 13px;
    font-style: italic;
    color: #333;
  }
  &::before {
    content: " ";
    border-top: 1px solid #ddd;
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    height: 1px;
    z-index: 1;
  }
}
.corporate-login-signup {
  font-size: 13px;
  text-align: center;
}

@media (max-width: 480px) {
  .country-menu {
    width: 280px;
  }
}
</style>


// WEBPACK FOOTER //
// EmailMobileForm.vue?dd9face8